import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useSnackbar } from "notistack";
import { useWhatsappTemplates } from "../../hooks/usePlanSummary";
import {
  useWhatsappNumbers,
  useCheckPaymentLinkRecipients,
  useSendPaymentLinksOnWhatsapp,
} from "../../hooks/useWhatsapp";
import { countBodyVariables, bodyPreview, isSendableTemplate } from "../../utils/whatsappTemplate";

// Same cap the server enforces per send (MAX_PAYMENT_LINK_ROWS in routes/whatsappCampaigns.js).
const MAX_ROWS = 500;

// What each template {{n}} can be filled with. Resolved per recipient on the server — every
// creator/vendor gets their OWN link, name and amount — and mirrored here only for the preview.
const VARIABLE_OPTIONS = [
  { value: "recipientName", label: "Recipient name" },
  { value: "creatorName", label: "Creator name" },
  { value: "paymentLink", label: "Payment link" },
  { value: "amount", label: "Amount" },
  { value: "campaignName", label: "Campaign name" },
  { value: "pocName", label: "POC name (who added the row)" },
  { value: "senderName", label: "Sender name (you)" },
  { value: "custom", label: "Custom text" },
];

// Sensible starting point, in the order a payment-details template usually reads:
// "Hi {{1}} (name), ... the {{2}} (campaign) ... link {{3}} ... Regards, {{4}} (sender)".
// Always keeps the payment link in the mix, since sending is blocked without it.
const DEFAULT_ORDER = ["recipientName", "campaignName", "paymentLink", "senderName", "amount"];

function defaultChoices(count) {
  if (count <= 0) return [];
  if (count === 1) return [{ kind: "paymentLink", text: "" }];
  if (count === 2) return [{ kind: "recipientName", text: "" }, { kind: "paymentLink", text: "" }];
  return Array.from({ length: count }, (_, i) => ({ kind: DEFAULT_ORDER[i] || "custom", text: "" }));
}

function resolvePreviewValues(choices, values, campaignName) {
  return choices.map((c) => {
    if (c.kind === "custom") return c.text;
    if (c.kind === "campaignName") return campaignName;
    return values?.[c.kind] ?? "";
  });
}

function PaymentLinkWhatsAppModal({ open, onClose, campaignId, campaignName, selectedQueueIds }) {
  const { enqueueSnackbar } = useSnackbar();
  const { data: templatesData, isLoading: templatesLoading } = useWhatsappTemplates("APPROVED");
  const { data: numbersData } = useWhatsappNumbers();
  const checkMutation = useCheckPaymentLinkRecipients();
  const sendMutation = useSendPaymentLinksOnWhatsapp();

  const [wabaId, setWabaId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [choices, setChoices] = useState([]);
  const [consent, setConsent] = useState(false);
  const [recipients, setRecipients] = useState(null);
  const [checkError, setCheckError] = useState("");
  const [paymentCampaignName, setPaymentCampaignName] = useState(campaignName || "");
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);

  const queueIds = useMemo(() => Array.from(selectedQueueIds || []), [selectedQueueIds]);

  // Ask the server who each selected row would actually go to (and which can't be sent, and
  // why) every time the dialog opens, so the warnings below are what the send will do.
  useEffect(() => {
    if (!open) return;
    setRecipients(null);
    setCheckError("");
    setResult(null);
    if (!queueIds.length) return;
    if (queueIds.length > MAX_ROWS) {
      setCheckError(`Select at most ${MAX_ROWS} rows per send — you selected ${queueIds.length}.`);
      return;
    }
    checkMutation.mutate(
      { campaignId, queueIds },
      {
        onSuccess: (data) => {
          setRecipients(data.recipients || []);
          if (data.campaignName) setPaymentCampaignName(data.campaignName);
        },
        onError: (error) => setCheckError(error?.response?.data?.message || "Couldn't check the selected rows"),
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const numbers = (numbersData?.tokens || []).filter((n) => n.isActive);
  const selectedNumber = numbers.find((n) => n.wabaId === wabaId) || null;

  useEffect(() => {
    if (!wabaId && numbers.length > 0) setWabaId(numbers[0].wabaId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numbers.length]);

  const allTemplates = templatesData?.templates || [];
  const templates = (wabaId ? allTemplates.filter((t) => t.wabaId === wabaId) : allTemplates).filter(isSendableTemplate);
  const selectedTemplate = useMemo(() => templates.find((t) => t._id === templateId) || null, [templates, templateId]);
  const variableCount = useMemo(() => countBodyVariables(selectedTemplate), [selectedTemplate]);

  const sendable = (recipients || []).filter((r) => r.canSend);
  const blocked = (recipients || []).filter((r) => !r.canSend);
  // Rows that share a link with an earlier row carry a reason but are still canSend — they
  // go out as one message, so they don't count as another recipient.
  const messageRows = sendable.filter((r) => !r.reason);
  const creatorMessages = messageRows.filter((r) => r.recipientType === "creator");
  const vendorMessages = messageRows.filter((r) => r.recipientType === "vendor");
  const needsOptInCount = creatorMessages.filter((r) => r.needsOptIn).length;
  const messageCount = messageRows.length;

  const wouldExceedLimit =
    selectedNumber && messageCount > selectedNumber.messagingLimit - selectedNumber.recentUniqueRecipients;

  const handleSelectNumber = (id) => {
    setWabaId(id);
    setTemplateId("");
    setChoices([]);
    setConsent(false);
  };

  const handleSelectTemplate = (id) => {
    setTemplateId(id);
    setChoices(defaultChoices(countBodyVariables(templates.find((t) => t._id === id))));
  };

  const setChoiceAt = (i, patch) =>
    setChoices((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  const hasPaymentLinkVariable = choices.some((c) => c.kind === "paymentLink");
  const customTextMissing = choices.some((c) => c.kind === "custom" && !c.text.trim());

  const sampleCreator = creatorMessages[0] || sendable.find((r) => r.recipientType === "creator");
  const sampleVendor = vendorMessages[0] || sendable.find((r) => r.recipientType === "vendor");

  const handleClose = () => {
    if (sending) return;
    setTemplateId("");
    setChoices([]);
    setConsent(false);
    setResult(null);
    onClose();
  };

  const handleSend = async () => {
    if (!templateId || !sendable.length) return;
    setSending(true);
    try {
      const data = await sendMutation.mutateAsync({
        campaignId,
        queueIds: sendable.map((r) => r.queueId),
        templateId,
        variableMap: choices.map((c) => (c.kind === "custom" ? `custom:${c.text.trim()}` : c.kind)),
        consentConfirmed: true,
      });
      setResult({ sent: data.sent || [], failed: data.failed || [] });
      if (!(data.failed || []).length) {
        enqueueSnackbar(`Sent to ${(data.sent || []).length} recipient${(data.sent || []).length === 1 ? "" : "s"}`, { variant: "success" });
      }
    } catch (error) {
      const data = error?.response?.data;
      if (data?.failed?.length) setResult({ sent: [], failed: data.failed });
      enqueueSnackbar(data?.message || "Failed to send messages", { variant: "error" });
    } finally {
      setSending(false);
    }
  };

  const renderPreview = (label, sample) => {
    if (!sample || !selectedTemplate) return null;
    return (
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>
          Preview — {label} ({sample.recipientName})
        </Typography>
        <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#F3FDF6", border: "1px solid #C8F0D4", fontSize: 13.5, wordBreak: "break-word" }}>
          {bodyPreview(selectedTemplate, resolvePreviewValues(choices, sample.values, paymentCampaignName)) || "—"}
        </Box>
      </Box>
    );
  };

  const checking = checkMutation.isPending || (recipients === null && !checkError && open);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
        <WhatsAppIcon sx={{ color: "#25D366" }} />
        Send payment link on WhatsApp
      </DialogTitle>
      <DialogContent>
        {templatesLoading || checking ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : checkError ? (
          <Alert severity="error" sx={{ mt: 1 }}>{checkError}</Alert>
        ) : numbers.length === 0 ? (
          <Alert severity="info" sx={{ mt: 1 }}>No WhatsApp number registered yet.</Alert>
        ) : result ? (
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {result.sent.length > 0 && (
              <Alert severity="success">
                Sent to {result.sent.length} recipient{result.sent.length === 1 ? "" : "s"}.
              </Alert>
            )}
            {result.failed.length > 0 && (
              <Alert severity="warning">
                {result.failed.length} not sent:
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {result.failed.map((f, i) => (
                    <li key={`${f.influencerId}-${i}`}>
                      {f.name} — {f.reason}
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
            <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>
              This send is tracked as a WhatsApp Campaign — see delivered/read/replied status on the{" "}
              <a href="/whatsapp/campaigns" target="_blank" rel="noreferrer">WhatsApp Campaigns</a> page.
            </Typography>
          </Stack>
        ) : (
          <>
            <Alert severity="info" sx={{ mt: 1, mb: 2, fontSize: 12.5 }}>
              <b>{creatorMessages.length}</b> creator{creatorMessages.length === 1 ? "" : "s"} and <b>{vendorMessages.length}</b>{" "}
              vendor{vendorMessages.length === 1 ? "" : "s"} will get a message with their own link. Rows with a vendor
              go to the vendor; the rest go to the creator. The link opens a form for payment details — Finance pays outside the app.
            </Alert>

            {blocked.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2, fontSize: 12.5 }}>
                {blocked.length} selected row{blocked.length === 1 ? "" : "s"} won't be sent:
                <Box component="ul" sx={{ m: 0, pl: 2.5, maxHeight: 120, overflowY: "auto" }}>
                  {blocked.map((r) => (
                    <li key={r.queueId}>
                      {r.recipientName} — {r.reason}
                    </li>
                  ))}
                </Box>
              </Alert>
            )}

            {messageCount === 0 ? (
              <Alert severity="error">None of the selected rows can be sent.</Alert>
            ) : (
              <>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel id="plwa-number-label">WhatsApp Number</InputLabel>
                  <Select
                    labelId="plwa-number-label"
                    label="WhatsApp Number"
                    value={wabaId}
                    onChange={(e) => handleSelectNumber(e.target.value)}
                  >
                    {numbers.map((n) => (
                      <MenuItem key={n.wabaId} value={n.wabaId}>{n.displayPhoneNumber}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedNumber && (
                  <Typography sx={{ fontSize: 11.5, color: wouldExceedLimit ? "#C0392B" : "#9CA3AF", mb: 2, mt: -1 }}>
                    {selectedNumber.recentUniqueRecipients}/{selectedNumber.messagingLimit} unique recipients reached on this
                    number in the last 24h
                    {wouldExceedLimit && ` — sending to ${messageCount} more would exceed the remaining headroom; the excess will be skipped, not silently over-sent`}
                  </Typography>
                )}

                {templates.length === 0 ? (
                  <Alert severity="info">
                    No approved WhatsApp templates for this number yet. Create one on the Templates page (for example
                    "Hi {"{{1}}"}, please submit your payment details here: {"{{2}}"}") and wait for Meta's review.
                  </Alert>
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel id="plwa-template-label">Template</InputLabel>
                    <Select
                      labelId="plwa-template-label"
                      label="Template"
                      value={templateId}
                      onChange={(e) => handleSelectTemplate(e.target.value)}
                    >
                      {templates.map((t) => (
                        <MenuItem key={t._id} value={t._id}>{t.name} ({t.language})</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {selectedTemplate && (
                  <Box sx={{ mt: 2 }}>
                    {Array.from({ length: variableCount }).map((_, i) => (
                      <Stack key={i} direction="row" spacing={1} sx={{ mb: 1.5 }}>
                        <FormControl size="small" sx={{ minWidth: 190 }}>
                          <InputLabel id={`plwa-var-${i}`}>{`Variable {{${i + 1}}}`}</InputLabel>
                          <Select
                            labelId={`plwa-var-${i}`}
                            label={`Variable {{${i + 1}}}`}
                            value={choices[i]?.kind || ""}
                            onChange={(e) => setChoiceAt(i, { kind: e.target.value })}
                          >
                            {VARIABLE_OPTIONS.map((o) => (
                              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {choices[i]?.kind === "custom" && (
                          <TextField
                            fullWidth
                            size="small"
                            label="Text"
                            value={choices[i].text}
                            onChange={(e) => setChoiceAt(i, { text: e.target.value })}
                          />
                        )}
                      </Stack>
                    ))}

                    {!hasPaymentLinkVariable && (
                      <Alert severity="warning" sx={{ mb: 1.5, fontSize: 12.5 }}>
                        Map one variable to <b>Payment link</b> — otherwise the message wouldn't contain the link.
                      </Alert>
                    )}

                    <Divider sx={{ my: 1.5 }} />
                    {renderPreview("creator", sampleCreator)}
                    {renderPreview("vendor", sampleVendor)}

                    <FormControlLabel
                      sx={{ mt: 1 }}
                      control={<Checkbox size="small" checked={consent} onChange={(e) => setConsent(e.target.checked)} />}
                      label={
                        <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>
                          I confirm these creators and vendors have agreed to receive WhatsApp messages from us
                          {needsOptInCount > 0 &&
                            ` — checking this also records consent for the ${needsOptInCount} creator${needsOptInCount === 1 ? "" : "s"} with no opt-in on file yet`}
                          . Messaging without real consent drives up block rate, which is what risks this number's quality
                          rating and messaging limit.
                        </Typography>
                      }
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none", borderRadius: "10px" }} disabled={sending}>
          {result ? "Close" : "Cancel"}
        </Button>
        {!result && messageCount > 0 && templates.length > 0 && (
          <Button
            onClick={handleSend}
            variant="contained"
            disabled={!templateId || !consent || !hasPaymentLinkVariable || customTextMissing || sending}
            sx={{ textTransform: "none", borderRadius: "10px", boxShadow: "none", bgcolor: "#25D366", "&:hover": { bgcolor: "#1FAF57" } }}
          >
            {sending ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : `Send to ${messageCount}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default PaymentLinkWhatsAppModal;
